import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { DepositAccountDetails } from './ui/DepositAccountDetails/DepositAccountDetails';
import { DepositAccountPaymentDetails } from './ui/DepositAccountPaymentDetails/DepositAccountPaymentDetails';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { RedirectButton } from '@/components/RedirectButton/RedirectButton';
import { useInterval } from '@/hooks/useInterval';
import { DEPOSIT_ACCOUNT_STATUS_DB } from '@/const/wire';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { Button } from '@/components/Button/Button';
import { getInboundWireDetailsPath } from '../../InboundWire/path/path';
import { Box } from '@/components/Box/Box';
import { getDepositAccountsPath } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/path/path';
import { showSnackbarResponseError } from '@/helpers/helpers';
import {
  deactivateDepositAccount,
  loadDepositAccount,
} from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/_DepositAccountDetailsPage/api/api';
import { loadDepositAccountPaymentDetails } from '@/bundle/_OrgAdmin/pages/InboundWire/api/api';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const DepositAccountDetailsPage = () => {
  const queryClient = useQueryClient();
  const { navigateBack } = useStatefulNavigation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpenDeactivateAccountModal, setIsOpenDeactivateAccountModal] = useState(false);

  const { data: depositAccountData } = useQuery({
    queryKey: ['load_deposit_account', id],
    queryFn: () => loadDepositAccount(id),
  });

  const depositAccountStatus = depositAccountData?.body?.status;

  const { data: paymentDetailsData, isFetching: isFetchingPaymentDetails } = useQuery({
    queryKey: ['load_deposit_account_payment_details', id],
    queryFn: () => loadDepositAccountPaymentDetails(id),
    enabled: !!(
      depositAccountStatus === DEPOSIT_ACCOUNT_STATUS_DB.ACTIVE ||
      depositAccountStatus === DEPOSIT_ACCOUNT_STATUS_DB.INACTIVE
    ),
  });

  const { mutate, isPending: isPendingDeactivateDepositAccount } = useMutation({
    mutationKey: ['deactivate_deposit_account'],
    mutationFn: () => {
      return deactivateDepositAccount(id);
    },
    onSuccess(response) {
      if (response.error) {
        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.DeactivateDepositAccountSuccess);

      queryClient.setQueryData(['load_deposit_account', id], response);
    },
    onSettled() {
      setIsOpenDeactivateAccountModal(false);
    },
  });

  const onClose = () => {
    setIsOpenDeactivateAccountModal(false);
  };

  const openDeactivateDepositAccountDialog = () => {
    setIsOpenDeactivateAccountModal(true);

    amplitudeService.logEvent(AMPLITUDE_EVENTS.DeactivateDepositAccountStartRedirect);
  };

  const depositAccount = depositAccountData?.body;
  const paymentDetails = paymentDetailsData?.body;
  const hasReturnToWireButton = location?.state?.transactionId;
  const isPendingStatus = depositAccountStatus === DEPOSIT_ACCOUNT_STATUS_DB.PENDING;
  const isAllowedToDeactivate = depositAccountStatus === DEPOSIT_ACCOUNT_STATUS_DB.ACTIVE;

  const redirectToWire = () => {
    navigate(getInboundWireDetailsPath(location.state?.transactionId, location.state?.wireId));
  };

  const redirectToDepositAccounts = () => {
    navigateBack(getDepositAccountsPath());
  };

  const { cancelInterval, isIntervalRunning } = useInterval(() => {
    if (isPendingStatus) {
      queryClient.invalidateQueries({ queryKey: ['load_deposit_account', id] });
      queryClient.invalidateQueries({ queryKey: ['load_deposit_account_payment_details', id] });
    }
  });

  useEffect(() => {
    const shouldCancelInterval = depositAccount && !isPendingStatus && isIntervalRunning;

    if (shouldCancelInterval) {
      cancelInterval();
    }
  }, [cancelInterval, isIntervalRunning, isPendingStatus, depositAccount]);

  return (
    <ContentLayout
      title='Receiving Account Details'
      breadcrumbs='Receiving Accounts > Details'
      backButton='Back to Receiving Accounts'
      onBack={redirectToDepositAccounts}
      headerAction={
        isAllowedToDeactivate && (
          <Button variant='secondary' width={160} mobileStretch onClick={openDeactivateDepositAccountDialog}>
            Deactivate
          </Button>
        )
      }
      subtitle={
        <>
          This receiving account is stored in the WireVault blockchain and cannot be edited. <br />
          You can deactivate this account if it's no longer being used.
        </>
      }
      apiError={depositAccountData?.error}
    >
      <PageBody>
        <Box display='flex' flexDirection='column' rowGap='28px'>
          <DepositAccountDetails depositAccount={depositAccount} />
          {depositAccountStatus && (
            <DepositAccountPaymentDetails
              status={depositAccount?.status}
              paymentDetails={paymentDetails}
              error={paymentDetailsData?.error}
              isLoading={isFetchingPaymentDetails}
            />
          )}
        </Box>
      </PageBody>
      <ConfirmModal
        isOpen={isOpenDeactivateAccountModal}
        header='Deactivate Receiving Account'
        body='You will not be able to use this receiving account once you deactivate it.'
        isLoading={isPendingDeactivateDepositAccount}
        onConfirm={mutate}
        onClose={onClose}
      />
      {hasReturnToWireButton && <RedirectButton onNavigate={redirectToWire} />}
    </ContentLayout>
  );
};
